class SlideshowCircularControls extends HTMLElement {
  constructor() {
    super();
    this.currentIndex = 0;
    this.timer = null;
    this.progressTimer = null;
    this.dots = [];
    this.isPlaying = false;
    this.isPaused = false;
    this.slideshowComponent = null;
    this.autoplayInterval = 4000; // default 4 seconds
    
    this.init();
  }

  init() {
    this.setupElements();
    this.setupEventListeners();
    this.updatePlayPauseButtons();
  }

  setupElements() {
    this.slideshowComponent = this.closest('slideshow-component');
    this.dots = Array.from(this.querySelectorAll('.slideshow-control--progressive'));
    
    // Get autoplay interval from slideshow component
    if (this.slideshowComponent) {
      const autoplayAttr = this.slideshowComponent.getAttribute('autoplay');
      if (autoplayAttr) {
        this.autoplayInterval = parseInt(autoplayAttr) * 1000;
        this.isPlaying = true;
      }
    }
  }

  setupEventListeners() {
    // Control navigation
    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', (e) => this.handleDotClick(e, index));
    });

    // Listen for slideshow events
    if (this.slideshowComponent) {
      this.slideshowComponent.addEventListener('slideshow:select', (e) => {
        this.handleSlideChange(e.detail.index, e.detail.previousIndex, e.detail.userInitiated);
      });

      this.slideshowComponent.addEventListener('slideshow:pause', () => {
        this.pause();
      });

      this.slideshowComponent.addEventListener('slideshow:play', () => {
        this.play();
      });
    }

    // Mouse events for pause on hover
    this.addEventListener('mouseenter', () => this.handleMouseEnter());
    this.addEventListener('mouseleave', () => this.handleMouseLeave());

    // Window events
    window.addEventListener('blur', () => this.pause());
    window.addEventListener('focus', () => this.handleWindowFocus());
    document.addEventListener('visibilitychange', () => this.handleVisibilityChange());
  }

  handleDotClick(event, index) {
    event.preventDefault();
    
    if (index === this.currentIndex) {
      // Clicking current dot toggles play/pause
      this.togglePlayPause();
    } else {
      // Navigate to different slide
      if (this.slideshowComponent) {
        this.slideshowComponent.select(index, event);
      }
    }
  }

  handleSlideChange(index, previousIndex, userInitiated) {
    if (index === this.currentIndex) return;
    
    this.currentIndex = index;

    // Update dots
    this.dots.forEach((dot, i) => {
      const isActive = i === index;
      dot.classList.toggle('active', isActive);
      dot.setAttribute('aria-selected', isActive);
      
      // Reset progress rings
      const progressBar = dot.querySelector('.slideshow-control__progress-bar');
      if (progressBar) {
        progressBar.style.strokeDashoffset = '100';
        progressBar.style.transition = 'none';
      }
    });

    this.updatePlayPauseButtons();

    // Restart timer if playing
    if (this.isPlaying && !this.isPaused) {
      this.startTimer();
    }
  }

  handleMouseEnter() {
    if (this.isPlaying) {
      this.pause();
      this.wasPlayingBeforeHover = true;
    }
  }

  handleMouseLeave() {
    if (this.wasPlayingBeforeHover) {
      this.play();
      this.wasPlayingBeforeHover = false;
    }
  }

  handleWindowFocus() {
    if (this.slideshowComponent && this.slideshowComponent.getAttribute('autoplay') && !this.isPaused) {
      this.play();
    }
  }

  handleVisibilityChange() {
    if (document.hidden) {
      this.pause();
    } else if (this.slideshowComponent && this.slideshowComponent.getAttribute('autoplay') && !this.isPaused) {
      this.play();
    }
  }

  play() {
    if (this.isPlaying && !this.isPaused) return;
    
    this.isPlaying = true;
    this.isPaused = false;
    this.updatePlayPauseButtons();
    this.startTimer();
  }

  pause() {
    this.isPlaying = false;
    this.clearTimers();
    this.updatePlayPauseButtons();
    
    // Pause current progress animation
    const currentDot = this.dots[this.currentIndex];
    if (currentDot) {
      const progressBar = currentDot.querySelector('.slideshow-control__progress-bar');
      if (progressBar) {
        const computedStyle = getComputedStyle(progressBar);
        const currentDashoffset = computedStyle.strokeDashoffset;
        progressBar.style.strokeDashoffset = currentDashoffset;
        progressBar.style.transition = 'none';
      }
    }
  }

  togglePlayPause() {
    if (this.isPlaying) {
      this.isPaused = true;
      this.pause();
      
      // Pause the slideshow component
      if (this.slideshowComponent && this.slideshowComponent.pause) {
        this.slideshowComponent.pause();
      }
    } else {
      this.isPaused = false;
      this.play();
      
      // Resume the slideshow component  
      if (this.slideshowComponent && this.slideshowComponent.resume) {
        this.slideshowComponent.resume();
      }
    }
  }

  startTimer() {
    this.clearTimers();

    const currentDot = this.dots[this.currentIndex];
    if (!currentDot) return;

    const progressBar = currentDot.querySelector('.slideshow-control__progress-bar');

    if (progressBar) {
      // Configura a animação usando stroke-dashoffset para precisão
      // O valor inicial deve ser 100 (barra vazia), e ao final 0 (barra cheia)
      progressBar.style.transition = 'none';
      progressBar.style.strokeDashoffset = '100';
      // Força o repaint para garantir que a transição será aplicada
      void progressBar.offsetWidth;
      progressBar.style.transition = `stroke-dashoffset ${this.autoplayInterval}ms linear`;
      progressBar.style.strokeDashoffset = '0';
    }

    // Timer para trocar o slide após o tempo correto
    this.timer = setTimeout(() => {
      if (this.slideshowComponent) {
        this.slideshowComponent.next();
      }
    }, this.autoplayInterval);
  }

  clearTimers() {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    if (this.progressTimer) {
      clearTimeout(this.progressTimer);
      this.progressTimer = null;
    }
  }

  updatePlayPauseButtons() {
    this.dots.forEach((dot, index) => {
      const playPauseContainer = dot.querySelector('.slideshow-control__play-pause');
      const playIcon = dot.querySelector('.icon-play');
      const pauseIcon = dot.querySelector('.icon-pause');
      
      if (index === this.currentIndex) {
        // Show play/pause button for current dot
        if (playPauseContainer) {
          playPauseContainer.style.display = 'flex';
        }
        
        if (this.isPlaying && !this.isPaused) {
          if (playIcon) playIcon.style.display = 'none';
          if (pauseIcon) pauseIcon.style.display = 'block';
        } else {
          if (playIcon) playIcon.style.display = 'block';
          if (pauseIcon) pauseIcon.style.display = 'none';
        }
      } else {
        // Hide play/pause button for other dots
        if (playPauseContainer) {
          playPauseContainer.style.display = 'none';
        }
      }
    });
  }

  disconnectedCallback() {
    this.clearTimers();
    
    window.removeEventListener('blur', () => this.pause());
    window.removeEventListener('focus', () => this.handleWindowFocus());
    document.removeEventListener('visibilitychange', () => this.handleVisibilityChange());
  }
}

// Register the custom element
if (!customElements.get('slideshow-controls')) {
  customElements.define('slideshow-controls', SlideshowCircularControls);
}